import { Modal } from '@lifebit-ai/lds'
import React from 'react'

import type { Result } from 'services/results/types'
import { getFileNameFromPath } from 'utils/string'

import FileContent from '../FileContent/FileContent'
import ApprovalMethodLabel from './ApprovalMethodLabel'
import * as styles from './AutomatedChecksReRun.styles'
import AutomatedChecksReport from './AutomatedChecksReport'
import useAutomatedChecksReRun from './useAutomatedChecksReRun'

interface AutomatedChecksReRunProps {
  resultId: string
  automatedChecks?: Result['automatedChecks']
  fileChangeStatus?: Result['fileChangeStatus']
  validationStatus?: Result['validationStatus']
  isAutomaticApproved?: boolean
  workspaceName?: string
}

const AutomatedChecksReRun: React.FC<AutomatedChecksReRunProps> = ({
  resultId,
  automatedChecks,
  fileChangeStatus,
  validationStatus,
  isAutomaticApproved,
  workspaceName
}) => {
  const automatedChecksReRunData = useAutomatedChecksReRun({
    resultId,
    automatedChecks,
    fileChangeStatus,
    workspaceName
  })

  if (!automatedChecksReRunData) return null

  const {
    checksResult,
    workspace,
    formattedRunDate,
    pipelineNameAndVersion,
    reportUrl,
    selectedReportPreview,
    isAutomatedChecksReportVisible,
    isAutoCheckJobRunning,
    isAutoCheckJobFailed,
    approvalMethodLabel,
    automatedChecksSkippedMessage,
    onClickViewAutomatedChecksReport,
    onCloseReportPreviewModal,
    isDownloadingReport
  } = automatedChecksReRunData

  return (
    <>
      <styles.Section>
        <styles.Title>Automated checks re-run</styles.Title>
        {automatedChecksSkippedMessage ? (
          <styles.SkippedMessage>{automatedChecksSkippedMessage}</styles.SkippedMessage>
        ) : (
          <styles.Details>
            <styles.Label>Airlock checks pipeline run date</styles.Label>
            <styles.Value>{formattedRunDate}</styles.Value>

            <styles.Label>Pipeline name and version used</styles.Label>
            <styles.Value>{pipelineNameAndVersion}</styles.Value>

            <styles.Label>Workspace</styles.Label>
            <styles.Value>{workspace}</styles.Value>

            <styles.Label>Checks result</styles.Label>
            <styles.ChecksResult>
              {checksResult.showStatusIcon && <styles.InfoIcon />}
              <styles.Value>{checksResult.label}</styles.Value>
            </styles.ChecksResult>

            <styles.Label>Approval method</styles.Label>
            <styles.Value>
              {isAutoCheckJobRunning ? (
                approvalMethodLabel
              ) : (
                <ApprovalMethodLabel validationStatus={validationStatus} isAutomaticApproved={isAutomaticApproved} />
              )}
            </styles.Value>

            {isAutomatedChecksReportVisible && (
              <AutomatedChecksReport
                reportUrl={reportUrl}
                isDownloadingReport={isDownloadingReport}
                isAutoCheckJobFailed={isAutoCheckJobFailed}
                onClickViewAutomatedChecksReport={onClickViewAutomatedChecksReport}
              />
            )}
          </styles.Details>
        )}
      </styles.Section>

      <Modal
        onCancel={onCloseReportPreviewModal}
        open={!!selectedReportPreview}
        title={
          selectedReportPreview
            ? getFileNameFromPath(selectedReportPreview.fileKey) ?? selectedReportPreview.fileKey
            : undefined
        }
        size="large"
        closable
      >
        {selectedReportPreview && (
          <FileContent fileKey={selectedReportPreview.fileKey} signedUrl={selectedReportPreview.signedUrl} />
        )}
      </Modal>
    </>
  )
}

export default AutomatedChecksReRun
